import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MdSend, MdArrowBack, MdAttachFile, MdCameraAlt, MdSearch, MdDescription,
  MdPerson, MdBadge, MdCategory, MdPriorityHigh, MdLocationOn,
  MdPhone, MdBloodtype, MdHome, MdContactPhone, MdCalendarMonth,
  MdCheckCircle, MdClose, MdCloudUpload, MdImage, MdSupervisorAccount,
  MdBusiness
} from 'react-icons/md';
import { helpdeskTicketApi, helpdeskCategoryApi, helpdeskSubCategoryApi, helpdeskLookupApi } from '../../../api/helpdeskApi';
import { toast } from 'react-toastify';
import './HelpdeskCreateTicket.css';

const PRIORITY_OPTIONS = [
  { value: 'LOW', label: 'Low', color: '#22c55e' },
  { value: 'MEDIUM', label: 'Medium', color: '#f59e0b' },
  { value: 'HIGH', label: 'High', color: '#f97316' },
  { value: 'CRITICAL', label: 'Critical', color: '#ef4444' }
];
const BLOOD_GROUP_OPTIONS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

// Allowed file types for SOC2/HITRUST compliance
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/jpg'];
const ALLOWED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png'];
const ALLOWED_ATTACHMENT_TYPES = [
  'image/jpeg', 'image/png', 'image/jpg',
  'application/pdf',
  'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain', 'text/csv'
];
const ALLOWED_ATTACHMENT_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt', '.csv'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

// Indian phone number validation: +91XXXXXXXXXX, 91XXXXXXXXXX, 0XXXXXXXXXX, or 10 digits starting with 6-9
const INDIAN_PHONE_REGEX = /^(?:\+91|91|0)?[6-9]\d{9}$/;

const validateIndianPhone = (value) => {
  if (!value || value.trim() === '') return true;
  const cleaned = value.replace(/[\s\-()]/g, '');
  return INDIAN_PHONE_REGEX.test(cleaned);
};

const formatPhoneInput = (value) => {
  return value.replace(/[^0-9+\s\-()]/g, '');
};

const validateFile = (file, allowedTypes, allowedExtensions, fieldName) => {
  if (!file) return null;
  if (file.size > MAX_FILE_SIZE) {
    return `${fieldName} exceeds maximum size of 10MB`;
  }
  if (!allowedTypes.includes(file.type)) {
    return `${fieldName} has invalid file type. Allowed: ${allowedExtensions.join(', ')}`;
  }
  const fileName = file.name.toLowerCase();
  const hasValidExt = allowedExtensions.some(ext => fileName.endsWith(ext));
  if (!hasValidExt) {
    return `${fieldName} has invalid extension. Allowed: ${allowedExtensions.join(', ')}`;
  }
  const parts = fileName.split('.');
  if (parts.length > 2) {
    const suspicious = parts.slice(0, -1).some(p => ['exe', 'bat', 'cmd', 'sh', 'ps1', 'vbs', 'js', 'html', 'htm', 'php', 'asp', 'jsp'].includes(p));
    if (suspicious) {
      return `${fieldName} has a suspicious filename. Please rename and try again.`;
    }
  }
  return null;
};

function HelpdeskCreateTicket() {
  const navigate = useNavigate();
  const [requestType, setRequestType] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const photoInputRef = useRef(null);
  const attachmentInputRef = useRef(null);

  // Employee search (on behalf)
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [employeeResults, setEmployeeResults] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    title: '', requestForType: 'Self', onBehalfName: '', onBehalfEmail: '',
    categoryId: '', subCategoryId: '', priority: 'MEDIUM',
    city: '', workLocationId: '', floor: '', cabinWorkstation: '', contactNumber: '',
    description: '',
    // ID Card fields
    employeeDob: '', employeeDoj: '', bloodGroup: '',
    residentialAddress: '', emergencyContact: '',
    // Profile fields
    employeeName: '', employeeDepartment: ''
  });

  const [attachment, setAttachment] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  // Lookups
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    loadCategories();
    loadCities();
    loadDepartments();
  }, []);

  const loadCategories = async () => {
    try {
      const cats = await helpdeskCategoryApi.getActive();
      setCategories(Array.isArray(cats) ? cats : []);
    } catch (e) { console.error(e); }
  };

  const loadCities = async () => {
    try {
      const c = await helpdeskLookupApi.getCities();
      setCities(Array.isArray(c) ? c : []);
    } catch (e) { console.error(e); }
  };

  const loadDepartments = async () => {
    try {
      const depts = await helpdeskLookupApi.getDepartments();
      setDepartments(Array.isArray(depts) ? depts : []);
    } catch (e) { console.error(e); }
  };

  // Debounced employee search
  const searchEmployees = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setEmployeeResults([]);
      setShowDropdown(false);
      return;
    }
    setIsSearching(true);
    try {
      const results = await helpdeskLookupApi.searchEmployees(term);
      setEmployeeResults(Array.isArray(results) ? results : []);
      setShowDropdown(true);
    } catch (e) {
      console.error(e);
      setEmployeeResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (employeeSearch && !selectedEmployee) {
        searchEmployees(employeeSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [employeeSearch, selectedEmployee, searchEmployees]);

  const handleSelectEmployee = async (emp) => {
    setSelectedEmployee(emp);
    setEmployeeSearch(`${emp.name} (${emp.empId})`);
    setShowDropdown(false);
    setEmployeeResults([]);
    // Pre-populate profile fields from the selected employee
    setFormData(prev => ({
      ...prev,
      employeeName: emp.name || '',
      employeeDepartment: emp.department || ''
    }));
    // If employee has a location, try to find their city and load locations
    if (emp.location) {
      try {
        const allCities = await helpdeskLookupApi.getCities();
        // Try each city to find the one containing this employee's location
        for (const city of (Array.isArray(allCities) ? allCities : [])) {
          const locs = await helpdeskLookupApi.getLocationsByCity(city);
          const matched = (Array.isArray(locs) ? locs : []).find(l => l.locationName === emp.location);
          if (matched) {
            setLocations(locs);
            setFormData(prev => ({ ...prev, city, workLocationId: matched.id }));
            break;
          }
        }
      } catch (e) { console.error(e); }
    }
  };

  const handleClearEmployee = () => {
    setSelectedEmployee(null);
    setEmployeeSearch('');
    setEmployeeResults([]);
    setShowDropdown(false);
    setFormData(prev => ({
      ...prev,
      employeeName: '', employeeDepartment: '',
      city: '', workLocationId: ''
    }));
    setLocations([]);
  };

  const handleCategoryChange = async (categoryId) => {
    setFormData({ ...formData, categoryId, subCategoryId: '' });
    if (categoryId) {
      try {
        const subs = await helpdeskSubCategoryApi.getByCategory(categoryId);
        setSubCategories(Array.isArray(subs) ? subs : []);
      } catch (e) { setSubCategories([]); }
    } else {
      setSubCategories([]);
    }
  };

  const handleCityChange = async (city) => {
    setFormData({ ...formData, city, workLocationId: '' });
    if (city) {
      try {
        const locs = await helpdeskLookupApi.getLocationsByCity(city);
        setLocations(Array.isArray(locs) ? locs : []);
      } catch (e) { setLocations([]); }
    } else {
      setLocations([]);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const error = validateFile(file, ALLOWED_IMAGE_TYPES, ALLOWED_IMAGE_EXTENSIONS, 'Photo');
      if (error) {
        toast.error(error);
        e.target.value = '';
        return;
      }
      setPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setPhoto(null);
    setPhotoPreview(null);
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const removeAttachment = () => {
    setAttachment(null);
    if (attachmentInputRef.current) attachmentInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!confirmed) {
      toast.warning('Please confirm the information is correct');
      return;
    }
    if (!selectedEmployee) {
      toast.warning('Please select an employee to create the ticket for');
      return;
    }

    // Validate phone numbers (Indian format)
    if (formData.contactNumber && !validateIndianPhone(formData.contactNumber)) {
      toast.error('Please enter a valid Indian phone number (10 digits starting with 6-9)');
      return;
    }
    if (formData.emergencyContact && !validateIndianPhone(formData.emergencyContact)) {
      toast.error('Please enter a valid Indian emergency contact number (10 digits starting with 6-9)');
      return;
    }

    setIsSubmitting(true);
    try {
      const submitData = new FormData();
      const requestData = {
        requestType,
        ...formData,
        requesterId: selectedEmployee.id,
        requestForType: 'On Behalf',
        onBehalfName: selectedEmployee.name,
        onBehalfEmail: selectedEmployee.email
      };
      submitData.append('data', new Blob([JSON.stringify(requestData)], { type: 'application/json' }));
      if (attachment) submitData.append('attachment', attachment);
      if (photo) submitData.append('photo', photo);

      await helpdeskTicketApi.create(submitData);
      toast.success('Ticket created successfully on behalf of ' + selectedEmployee.name);
      navigate('/admin/helpdesk/dashboard');
    } catch (e) {
      const msg = e.response?.data?.message || e.message || 'Failed to create ticket';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Type selection screen
  if (!requestType) {
    return (
      <div className="admin-create-ticket">
        {/* Stepper */}
        <div className="form-stepper">
          <div className="step active">
            <div className="step-number">1</div>
            <span className="step-label">Select Type</span>
          </div>
          <div className="step-line" />
          <div className="step">
            <div className="step-number">2</div>
            <span className="step-label">Fill Details</span>
          </div>
          <div className="step-line" />
          <div className="step">
            <div className="step-number">3</div>
            <span className="step-label">Submit</span>
          </div>
        </div>

        <div className="type-selection">
          <div className="type-selection-header">
            <div className="admin-badge"><MdSupervisorAccount /> Admin — Create on Behalf</div>
            <h2>Create Helpdesk Ticket</h2>
            <p className="subtitle">Create a ticket on behalf of an employee</p>
          </div>
          <div className="type-cards">
            <div className="type-card general-card" onClick={() => setRequestType('GENERAL_REQUEST')}>
              <div className="type-card-accent" />
              <div className="type-icon-circle general-bg">
                <MdDescription />
              </div>
              <h3>General Request</h3>
              <p>Submit a general service request for Admin/Facility department</p>
              <div className="type-card-arrow">Get Started &rarr;</div>
            </div>
            <div className="type-card idcard-card" onClick={() => setRequestType('ID_CARD_REQUEST')}>
              <div className="type-card-accent idcard-accent" />
              <div className="type-icon-circle idcard-bg">
                <MdBadge />
              </div>
              <h3>ID Card Request</h3>
              <p>Request a new employee ID card with photo</p>
              <div className="type-card-arrow">Get Started &rarr;</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-create-ticket">
      {/* Stepper */}
      <div className="form-stepper">
        <div className="step completed">
          <div className="step-number"><MdCheckCircle /></div>
          <span className="step-label">Select Type</span>
        </div>
        <div className="step-line active-line" />
        <div className="step active">
          <div className="step-number">2</div>
          <span className="step-label">Fill Details</span>
        </div>
        <div className="step-line" />
        <div className="step">
          <div className="step-number">3</div>
          <span className="step-label">Submit</span>
        </div>
      </div>

      {/* Header */}
      <div className="page-header">
        <button className="btn btn-back" onClick={() => setRequestType('')}>
          <MdArrowBack /> Back
        </button>
        <div className="header-info">
          <div className={`header-badge ${requestType === 'GENERAL_REQUEST' ? 'general-badge' : 'idcard-badge'}`}>
            {requestType === 'GENERAL_REQUEST' ? <MdDescription /> : <MdBadge />}
            {requestType === 'GENERAL_REQUEST' ? 'General Request' : 'ID Card Request'}
          </div>
          <span className="header-on-behalf">On Behalf of Employee</span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="ticket-form">
        {/* Employee Search Section */}
        <div className="form-section employee-section">
          <div className="section-header">
            <div className="section-icon employee-section-icon"><MdSupervisorAccount /></div>
            <div>
              <h3>Select Employee</h3>
              <p>Search and select the employee this ticket is being created for</p>
            </div>
          </div>

          <div className="employee-search-wrapper">
            <div className="search-input-container">
              <MdSearch className="search-icon" />
              <input
                type="text"
                value={employeeSearch}
                onChange={(e) => {
                  setEmployeeSearch(e.target.value);
                  if (selectedEmployee) setSelectedEmployee(null);
                }}
                placeholder="Search by name, employee ID, or email..."
                className="employee-search-input"
              />
              {selectedEmployee && (
                <button type="button" className="clear-employee-btn" onClick={handleClearEmployee}>Clear</button>
              )}
            </div>
            {showDropdown && employeeResults.length > 0 && (
              <div className="employee-dropdown">
                {employeeResults.map((emp) => (
                  <div key={emp.id} className="employee-option" onClick={() => handleSelectEmployee(emp)}>
                    <div className="emp-option-avatar"><MdPerson /></div>
                    <div>
                      <div className="emp-name">{emp.name}</div>
                      <div className="emp-details">{emp.empId} &bull; {emp.email} &bull; {emp.department}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {showDropdown && employeeResults.length === 0 && !isSearching && employeeSearch.length >= 2 && (
              <div className="employee-dropdown">
                <div className="no-results">No employees found</div>
              </div>
            )}
            {isSearching && (
              <div className="employee-dropdown">
                <div className="no-results">Searching...</div>
              </div>
            )}
          </div>

          {selectedEmployee && (
            <div className="selected-employee-card">
              <div className="emp-avatar"><MdPerson /></div>
              <div className="emp-info">
                <div className="emp-info-name">{selectedEmployee.name}</div>
                <div className="emp-info-detail">
                  {selectedEmployee.empId} &bull; {selectedEmployee.email}
                  {selectedEmployee.department && <> &bull; {selectedEmployee.department}</>}
                  {selectedEmployee.location && <> &bull; {selectedEmployee.location}</>}
                </div>
              </div>
              <button type="button" className="emp-change-btn" onClick={handleClearEmployee}>Change</button>
            </div>
          )}
        </div>

        {/* EMPLOYEE DETAILS (common for both request types, shown when employee is selected) */}
        {selectedEmployee && (
          <div className="form-section">
            <div className="section-header">
              <div className="section-icon general-section-icon"><MdPerson /></div>
              <div>
                <h3>Employee Details</h3>
                <p>Verify and update the employee's information</p>
              </div>
            </div>

            <div className="form-row cols-3">
              <div className="form-group">
                <label><MdBadge /> Employee ID / WorkDay ID</label>
                <input type="text" value={selectedEmployee.empId || ''} readOnly className="readonly-field" />
              </div>
              <div className="form-group">
                <label><MdPerson /> Name</label>
                <input type="text" value={formData.employeeName || ''} readOnly className="readonly-field" />
              </div>
              <div className="form-group">
                <label><MdBusiness /> Department <span className="required">*</span></label>
                {selectedEmployee?.department && selectedEmployee.department !== 'General' ? (
                  <input type="text" value={formData.employeeDepartment || ''} readOnly className="readonly-field" />
                ) : (
                  <select value={formData.employeeDepartment} onChange={e => handleChange('employeeDepartment', e.target.value)} required>
                    <option value="">Select Department...</option>
                    {departments.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                )}
              </div>
            </div>
          </div>
        )}

        {/* GENERAL REQUEST FORM */}
        {requestType === 'GENERAL_REQUEST' && (
          <>
            {/* Section: Request Details */}
            <div className="form-section">
              <div className="section-header">
                <div className="section-icon general-section-icon"><MdDescription /></div>
                <div>
                  <h3>Request Details</h3>
                  <p>Provide the basic information about the request</p>
                </div>
              </div>

              <div className="form-group">
                <label>Title <span className="required">*</span></label>
                <input type="text" value={formData.title} onChange={e => handleChange('title', e.target.value)} required placeholder="Brief description of the request" />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea value={formData.description} onChange={e => handleChange('description', e.target.value)} rows={4} placeholder="Describe the request in detail..." />
              </div>
            </div>

            {/* Section: Classification */}
            <div className="form-section">
              <div className="section-header">
                <div className="section-icon classify-section-icon"><MdCategory /></div>
                <div>
                  <h3>Classification</h3>
                  <p>Categorize and prioritize the request</p>
                </div>
              </div>

              <div className="form-row cols-2">
                <div className="form-group">
                  <label><MdCategory /> Category</label>
                  <select value={formData.categoryId} onChange={e => handleCategoryChange(e.target.value)}>
                    <option value="">Select Category...</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.categoryName}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Sub Category</label>
                  <select value={formData.subCategoryId} onChange={e => handleChange('subCategoryId', e.target.value)} disabled={!formData.categoryId}>
                    <option value="">Select Sub Category...</option>
                    {subCategories.map(s => <option key={s.id} value={s.id}>{s.subCategoryName}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label><MdPriorityHigh /> Priority Level</label>
                <div className="priority-selector">
                  {PRIORITY_OPTIONS.map(p => (
                    <button
                      key={p.value}
                      type="button"
                      className={`priority-btn ${formData.priority === p.value ? 'selected' : ''}`}
                      style={{ '--priority-color': p.color }}
                      onClick={() => handleChange('priority', p.value)}
                    >
                      <span className="priority-dot" />
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Section: Location & Contact */}
            <div className="form-section">
              <div className="section-header">
                <div className="section-icon location-section-icon"><MdLocationOn /></div>
                <div>
                  <h3>Location & Contact</h3>
                  <p>Employee location and contact details</p>
                </div>
              </div>

              <div className="form-row cols-3">
                <div className="form-group">
                  <label><MdLocationOn /> City <span className="required">*</span></label>
                  <select value={formData.city} onChange={e => handleCityChange(e.target.value)} required>
                    <option value="">Select City...</option>
                    {cities.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Work Location <span className="required">*</span></label>
                  <select value={formData.workLocationId} onChange={e => handleChange('workLocationId', e.target.value)} disabled={!formData.city} required>
                    <option value="">Select Location...</option>
                    {locations.map(l => <option key={l.id} value={l.id}>{l.locationName}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label><MdLocationOn /> Floor <span className="required">*</span></label>
                  <input type="text" value={formData.floor} onChange={e => handleChange('floor', e.target.value)} required placeholder="e.g., Ground Floor, 1st Floor" />
                </div>
              </div>

              <div className="form-row cols-2">
                <div className="form-group">
                  <label>Cabin / Workstation</label>
                  <input type="text" value={formData.cabinWorkstation} onChange={e => handleChange('cabinWorkstation', e.target.value)} placeholder="e.g., Cabin 201 / WFH" />
                </div>
                <div className="form-group" />
              </div>

              <div className="form-row cols-2">
                <div className="form-group">
                  <label><MdPhone /> Contact Number</label>
                  <input type="tel" value={formData.contactNumber} maxLength={13}
                    onChange={e => handleChange('contactNumber', formatPhoneInput(e.target.value))}
                    placeholder="e.g., +91 98765 43210" />
                  {formData.contactNumber && !validateIndianPhone(formData.contactNumber) && (
                    <span className="field-error">Enter a valid Indian number (10 digits starting with 6-9)</span>
                  )}
                </div>
                <div className="form-group" />
              </div>
            </div>

            {/* Section: Attachment */}
            <div className="form-section">
              <div className="section-header">
                <div className="section-icon attach-section-icon"><MdAttachFile /></div>
                <div>
                  <h3>Attachment</h3>
                  <p>Upload any supporting documents (optional, max 10MB)</p>
                </div>
              </div>

              <div className="upload-zone" onClick={() => attachmentInputRef.current?.click()}>
                <input type="file" ref={attachmentInputRef} onChange={e => {
                  const file = e.target.files[0];
                  if (file) {
                    const error = validateFile(file, ALLOWED_ATTACHMENT_TYPES, ALLOWED_ATTACHMENT_EXTENSIONS, 'Attachment');
                    if (error) {
                      toast.error(error);
                      e.target.value = '';
                      return;
                    }
                    setAttachment(file);
                  }
                }} accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" style={{ display: 'none' }} />
                {attachment ? (
                  <div className="upload-preview-file">
                    <MdAttachFile className="upload-file-icon" />
                    <div className="upload-file-info">
                      <span className="upload-file-name">{attachment.name}</span>
                      <span className="upload-file-size">{(attachment.size / 1024).toFixed(1)} KB</span>
                    </div>
                    <button type="button" className="upload-remove-btn" onClick={(e) => { e.stopPropagation(); removeAttachment(); }}>
                      <MdClose />
                    </button>
                  </div>
                ) : (
                  <div className="upload-placeholder">
                    <MdCloudUpload className="upload-cloud-icon" />
                    <span>Click to upload or drag and drop</span>
                    <span className="upload-hint">PDF, DOC, DOCX, XLS, XLSX, JPG, PNG, TXT, CSV (max 10MB)</span>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* ID CARD REQUEST FORM */}
        {requestType === 'ID_CARD_REQUEST' && (
          <>
            <div className="id-card-notice">
              <MdBadge className="notice-icon" />
              <div>
                <strong>ID Card Request</strong>
                <p>Please provide the additional details below required for the employee's ID card. Employee information is shown above.</p>
              </div>
            </div>

            {/* Section: Personal Details */}
            <div className="form-section">
              <div className="section-header">
                <div className="section-icon idcard-section-icon"><MdPerson /></div>
                <div>
                  <h3>Personal Details</h3>
                  <p>Required information for the employee's ID card</p>
                </div>
              </div>

              <div className="form-row cols-2">
                <div className="form-group">
                  <label><MdCalendarMonth /> Date of Birth <span className="required">*</span></label>
                  <input type="date" value={formData.employeeDob} onChange={e => handleChange('employeeDob', e.target.value)} required />
                </div>
                <div className="form-group">
                  <label><MdCalendarMonth /> Date of Joining <span className="required">*</span></label>
                  <input type="date" value={formData.employeeDoj} onChange={e => handleChange('employeeDoj', e.target.value)} required />
                </div>
              </div>

              <div className="form-row cols-3">
                <div className="form-group">
                  <label><MdBloodtype /> Blood Group <span className="required">*</span></label>
                  <select value={formData.bloodGroup} onChange={e => handleChange('bloodGroup', e.target.value)} required>
                    <option value="">Select Blood Group...</option>
                    {BLOOD_GROUP_OPTIONS.map(bg => <option key={bg} value={bg}>{bg}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label><MdPhone /> Contact Number</label>
                  <input type="tel" value={formData.contactNumber} maxLength={13}
                    onChange={e => handleChange('contactNumber', formatPhoneInput(e.target.value))}
                    placeholder="e.g., +91 98765 43210" />
                  {formData.contactNumber && !validateIndianPhone(formData.contactNumber) && (
                    <span className="field-error">Enter a valid Indian number (10 digits starting with 6-9)</span>
                  )}
                </div>
                <div className="form-group">
                  <label><MdContactPhone /> Emergency Contact <span className="required">*</span></label>
                  <input type="tel" value={formData.emergencyContact} maxLength={13}
                    onChange={e => handleChange('emergencyContact', formatPhoneInput(e.target.value))}
                    placeholder="e.g., +91 98765 43210" required />
                  {formData.emergencyContact && !validateIndianPhone(formData.emergencyContact) && (
                    <span className="field-error">Enter a valid Indian number (10 digits starting with 6-9)</span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label><MdHome /> Residential Address <span className="required">*</span></label>
                <textarea value={formData.residentialAddress} onChange={e => handleChange('residentialAddress', e.target.value)} rows={3} required placeholder="Full residential address for the ID card" />
              </div>
            </div>

            {/* Section: Location */}
            <div className="form-section">
              <div className="section-header">
                <div className="section-icon location-section-icon"><MdLocationOn /></div>
                <div>
                  <h3>Location</h3>
                  <p>Select the employee's city and work location</p>
                </div>
              </div>

              <div className="form-row cols-2">
                <div className="form-group">
                  <label><MdLocationOn /> City <span className="required">*</span></label>
                  <select value={formData.city} onChange={e => handleCityChange(e.target.value)} required>
                    <option value="">Select City...</option>
                    {cities.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Work Location <span className="required">*</span></label>
                  <select value={formData.workLocationId} onChange={e => handleChange('workLocationId', e.target.value)} disabled={!formData.city} required>
                    <option value="">Select Location...</option>
                    {locations.map(l => <option key={l.id} value={l.id}>{l.locationName}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-row cols-2">
                <div className="form-group">
                  <label><MdLocationOn /> Floor <span className="required">*</span></label>
                  <input type="text" value={formData.floor} onChange={e => handleChange('floor', e.target.value)} required placeholder="e.g., Ground Floor, 1st Floor, 2nd Floor" />
                </div>
                <div className="form-group" />
              </div>
            </div>

            {/* Section: Photo Upload */}
            <div className="form-section">
              <div className="section-header">
                <div className="section-icon photo-section-icon"><MdCameraAlt /></div>
                <div>
                  <h3>Photo Upload</h3>
                  <p>Upload a passport-size photo for the ID card (required, image only, max 10MB)</p>
                </div>
              </div>

              <div className="photo-upload-area">
                {photoPreview ? (
                  <div className="photo-preview-container">
                    <img src={photoPreview} alt="Preview" className="photo-preview-img" />
                    <div className="photo-preview-overlay">
                      <button type="button" className="photo-change-btn" onClick={() => photoInputRef.current?.click()}>
                        Change Photo
                      </button>
                      <button type="button" className="photo-remove-btn" onClick={removePhoto}>
                        <MdClose /> Remove
                      </button>
                    </div>
                    <div className="photo-file-name">{photo?.name}</div>
                  </div>
                ) : (
                  <div className="photo-upload-zone" onClick={() => photoInputRef.current?.click()}>
                    <MdImage className="photo-upload-icon" />
                    <span className="photo-upload-text">Click to upload photo</span>
                    <span className="photo-upload-hint">JPG, PNG or JPEG (max 10MB)</span>
                  </div>
                )}
                <input type="file" ref={photoInputRef} onChange={handlePhotoChange} accept=".jpg,.jpeg,.png" style={{ display: 'none' }} required={!photo} />
              </div>
            </div>
          </>
        )}

        {/* Confirm & Submit */}
        <div className="form-section confirm-section">
          <div className="confirm-checkbox">
            <input type="checkbox" id="admin-confirm-check" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
            <label htmlFor="admin-confirm-check">
              I confirm that all the information provided above is accurate and correct
            </label>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setRequestType('')}>
              <MdArrowBack /> Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting || !confirmed || !selectedEmployee}>
              {isSubmitting ? (
                <>
                  <span className="spinner" /> Creating...
                </>
              ) : (
                <>
                  <MdSend /> Create Ticket
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default HelpdeskCreateTicket;
